import { useState, useEffect } from "react";
import { Check, Type, Sparkles, Square, Zap } from "lucide-react";
import { cn } from "../lib/utils";

const CAPTION_STYLES = [
  {
    id: "classic",
    name: "Classic",
    description: "Clean white text with outline",
    icon: Type,
    preview: "Hello World",
    previewStyle: "text-white drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]",
    color: "#FFFFFF",
  },
  {
    id: "karaoke",
    name: "Karaoke",
    description: "Word-by-word highlight",
    icon: Sparkles,
    preview: "Hello World",
    previewStyle: "text-yellow-400 drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]",
    color: "#FFD700",
  },
  {
    id: "boxed",
    name: "Boxed",
    description: "Text with background box",
    icon: Square,
    preview: "Hello World",
    previewStyle: "bg-black/70 px-2 py-0.5 rounded text-white",
    color: "#00F0FF",
  },
  {
    id: "bold_impact",
    name: "Bold Impact",
    description: "Large, attention-grabbing",
    icon: Zap,
    preview: "HELLO WORLD",
    previewStyle: "text-yellow-300 font-black text-lg drop-shadow-[0_3px_3px_rgba(0,0,0,0.9)]",
    color: "#FF0055",
  },
];

export function CaptionStyleSelector({ 
  value = "classic", 
  onChange, 
  showPreview = true,
  compact = false 
}) {
  const [selectedStyle, setSelectedStyle] = useState(value);

  useEffect(() => {
    setSelectedStyle(value);
  }, [value]);

  const handleSelect = (styleId) => {
    setSelectedStyle(styleId);
    if (onChange) {
      onChange(styleId);
    }
  };

  if (compact) {
    return (
      <div className="flex gap-2 flex-wrap">
        {CAPTION_STYLES.map((style) => {
          const Icon = style.icon;
          const isSelected = selectedStyle === style.id;
          
          return (
            <button
              key={style.id}
              onClick={() => handleSelect(style.id)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg border transition-all",
                isSelected
                  ? "bg-cyan-500/20 border-cyan-500 text-white"
                  : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white"
              )}
              data-testid={`caption-style-${style.id}`}
            >
              <Icon className="w-4 h-4" style={{ color: isSelected ? style.color : undefined }} />
              <span className="text-sm font-medium">{style.name}</span>
              {isSelected && <Check className="w-4 h-4 text-cyan-400" />}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {CAPTION_STYLES.map((style) => {
        const Icon = style.icon;
        const isSelected = selectedStyle === style.id;
        
        return (
          <div
            key={style.id}
            onClick={() => handleSelect(style.id)}
            className={cn(
              "p-4 rounded-xl border-2 cursor-pointer transition-all",
              isSelected
                ? "border-cyan-500 bg-cyan-500/10"
                : "border-white/10 bg-white/5 hover:border-white/30"
            )}
            data-testid={`caption-style-${style.id}`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div 
                className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center",
                  isSelected ? "bg-cyan-500/20" : "bg-white/10"
                )}
              >
                <Icon 
                  className="w-5 h-5" 
                  style={{ color: isSelected ? style.color : "rgba(255,255,255,0.6)" }} 
                />
              </div>
              <div className="flex-1">
                <h4 className={cn(
                  "font-medium",
                  isSelected ? "text-white" : "text-white/80"
                )}>
                  {style.name}
                </h4>
                <p className="text-white/50 text-xs">{style.description}</p>
              </div>
              {isSelected && (
                <Check className="w-5 h-5 text-cyan-400" />
              )}
            </div>
            
            {showPreview && (
              <div className="h-12 bg-gradient-to-b from-slate-800 to-slate-900 rounded-lg flex items-center justify-center">
                <span className={cn("text-sm font-semibold", style.previewStyle)}>
                  {style.preview}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export { CAPTION_STYLES };
export default CaptionStyleSelector;
